/**
 * Edit permissions for the money/terms field groups on this screen. Fields listed
 * here are rendered read-only for users without the permission; the same map on
 * the backend (`servicing.constants.ts`) rejects the save. Keep the two in sync.
 */
export const fieldEditPermissions: Record<string, string> = {
  // Dates
  mortgageAcquisitionDate: 'Loan.Servicing.ServicingInformation.Dates.Edit',
  interestOnlyExpirationDate: 'Loan.Servicing.ServicingInformation.Dates.Edit',
  closingDate: 'Loan.Servicing.ServicingInformation.Dates.Edit',
  firstPaymentDate: 'Loan.Servicing.ServicingInformation.Dates.Edit',
  originalMatDate: 'Loan.Servicing.ServicingInformation.Dates.Edit',
  interestPaidThruDate: 'Loan.Servicing.ServicingInformation.Dates.Edit',
  curMatDate: 'Loan.Servicing.ServicingInformation.Dates.Edit',
  nextPaymentDate: 'Loan.Servicing.ServicingInformation.Dates.Edit',

  // Balances
  originalLoanAmount: 'Loan.Servicing.ServicingInformation.Balances.Edit',
  currentUpb: 'Loan.Servicing.ServicingInformation.Balances.Edit',
  escrowBalance: 'Loan.Servicing.ServicingInformation.Balances.Edit',
  suspenseBalance: 'Loan.Servicing.ServicingInformation.Balances.Edit',
  unpaidInterestBalance: 'Loan.Servicing.ServicingInformation.Balances.Edit',
  unpaidDefaultInterestBalance: 'Loan.Servicing.ServicingInformation.Balances.Edit',
  unpaidChargesBalance: 'Loan.Servicing.ServicingInformation.Balances.Edit',
  unpaidLateChargesBalance: 'Loan.Servicing.ServicingInformation.Balances.Edit',

  // Interest Calculations + Rate
  rateType: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  interestType: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  rateChangeAccrual: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  rateChangeRecast: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  dailyRateBasis: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  accrualPeriod: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  unpaidInterestAppliesTo: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  interestOnly: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  compoundInterest: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  interestOnUnpaidCharges: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  interestOnUnpaidLateCharges: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  autoPayFromInterestReserve: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  unusedFeeRate: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  unusedFeeCommitment: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  creditFacilityType: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  creditLimit: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  drawPeriodEndDate: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  repaymentTermMonths: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  minimumDrawAmount: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  originalInterestRate: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  currentInterestRate: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  soldRate: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  spreadPayee: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  armIndex: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  armIndexName: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  armIndexValue: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  armLookbackDays: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  armMargin: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  armFixedForMonths: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  armAdjustEveryMonths: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  armNextAdjustmentDate: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  armInitialCap: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  armPeriodicCap: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  armLifeCap: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  armFloor: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  armCeiling: 'Loan.Servicing.ServicingInformation.Terms.Edit',
  armRounding: 'Loan.Servicing.ServicingInformation.Terms.Edit',

  // Payments + Escrow
  paymentFrequency: 'Loan.Servicing.ServicingInformation.Payments.Edit',
  billingDate: 'Loan.Servicing.ServicingInformation.Payments.Edit',
  principalInterest: 'Loan.Servicing.ServicingInformation.Payments.Edit',
  paymentDueDay: 'Loan.Servicing.ServicingInformation.Payments.Edit',
  initialMonthlyPayment: 'Loan.Servicing.ServicingInformation.Payments.Edit',
  escrowMonthly: 'Loan.Servicing.ServicingInformation.Payments.Edit',
  initialAmountDisbursed: 'Loan.Servicing.ServicingInformation.Payments.Edit',
  totalPayment: 'Loan.Servicing.ServicingInformation.Payments.Edit',
  otherPayments: 'Loan.Servicing.ServicingInformation.Payments.Edit',
  escrowType: 'Loan.Servicing.ServicingInformation.Payments.Edit',
  initialEscrowDeposit: 'Loan.Servicing.ServicingInformation.Payments.Edit',
}

export const floodCertKeys = [
  'nfipCommunityDate',
  'floodProgram',
  'communityPanelNo',
  'floodZoneDetermination',
  'determinationDate',
  'determinationReportNo',
  'nfipCommunityNumber',
  'floodLomaLomr',
  'floodLifeOfLoan',
  'specialFloodHazardArea',
]

export const calcKeys = [
  'insurancePremium',
  'insuranceMonthlyPayment',
  'countyTaxMonthlyPayment',
  'totalEscrowPayment',
  'insuranceAmountCollected',
  'insuranceMonthCollected',
  'taxAmountCollected',
  'taxMonthCollected',
  'insuranceAgentName',
  'insuranceAgentAddress',
  'insuranceCompanyName',
  'insurancePhoneNumber',
  'insuranceEmail',
  'nfipCommunityDate',
  'floodProgram',
  'communityPanelNo',
  'floodZoneDetermination',
  'determinationDate',
  'determinationReportNo',
  'nfipCommunityNumber',
  'floodLomaLomr',
  'floodLifeOfLoan',
  'specialFloodHazardArea',
  'appraisalDate',
  'parcelNo',
  'asIsAppraisedValue',
  'asRepairedAppraisedValue',
  'asIsValue',
  'afterRepairValue',
  'parcelNo',
  'brokerCompanyName',
  'brokerName',
  'brokerPhone',
  'brokerEmail',
  'lenderCompanyName',
  'lenderName',
  'lenderPhone',
  'lenderEmail',
  'paymentFrequency',
  'currentUpb',
  'initialEscrowDeposit',
  'constructionBudget',
  'initialMonthlyPayment',
  'propertyStreetNumber',
  'propertyStreetDirection',
  'propertyStreetName',
  'propertyUnitNumber',
  'propertyCity',
  'propertyState',
  'propertyZipCode',
  'propertyCounty',
  'fipsCode',
]
